import { useCallback, useEffect, useState } from 'react'
import { Loader2, RefreshCw, Search, Upload } from 'lucide-react'
import { useNotification } from '../../../contexts/NotificationContext'
import {
  CONSENT_LABELS,
  MAILABLE_FILTER,
  SOURCE_LABELS,
  SUPPRESSED_FILTER,
  addSuppressions,
  fetchContactStats,
  fetchContacts,
  syncContacts,
} from '../../../services/crmService'
import { Button } from '../../ui/button'
import { ConfirmDialog } from '../../ui/confirm-dialog'
import { Input } from '../../ui/input'
import ContactStat from './ContactStat'
import ContactsSelectionBar from './ContactsSelectionBar'
import ContactsTable from './ContactsTable'
import ImportDialog from './ImportDialog'

const PAGE_SIZE = 50

function emptyMessageFor({ total, appliedSearch, source, consent }) {
  if (total === 0 && !appliedSearch && !source && !consent) {
    return 'No contacts yet. Run a sync or import a CSV to build the list.'
  }
  if (consent === MAILABLE_FILTER) {
    return 'Nobody is reachable right now — everyone is either unconsented, suppressed, or opted out.'
  }
  return 'No contacts match those filters.'
}

export default function ContactsView() {
  const notify = useNotification()
  const [stats, setStats] = useState(null)
  const [contacts, setContacts] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [source, setSource] = useState('')
  const [consent, setConsent] = useState('')
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  // Contact id -> email, so a selection can outlive the page it was made on.
  const [selected, setSelected] = useState(() => new Map())
  // The contacts awaiting a "yes" in the confirm dialog; null when closed.
  const [pendingSuppress, setPendingSuppress] = useState(null)

  const loadStats = useCallback(async () => {
    try {
      setStats(await fetchContactStats())
    } catch (err) {
      notify.error(err.message || 'Could not load contact totals')
    }
  }, [notify])

  const loadContacts = useCallback(async () => {
    setLoading(true)
    try {
      const result = await fetchContacts({ search: appliedSearch, source, consent, page, pageSize: PAGE_SIZE })
      setContacts(result.contacts)
      setTotal(result.total)
    } catch (err) {
      notify.error(err.message || 'Could not load contacts')
    } finally {
      setLoading(false)
    }
  }, [appliedSearch, source, consent, page, notify])

  useEffect(() => { loadContacts() }, [loadContacts])
  useEffect(() => { loadStats() }, [loadStats])

  const runSync = async () => {
    setSyncing(true)
    try {
      const result = await syncContacts(['app', 'shopify'])
      if (result?.setupRequired) {
        notify.warning(result.error || 'Shopify is not configured yet')
      } else {
        const app = result?.app?.imported ?? 0
        const shopify = result?.shopify?.imported ?? 0
        notify.success(`Synced ${app} app users and ${shopify} Shopify customers`)
        // A missing Shopify scope is the expected first-run state, so surface
        // it as its own message rather than burying it in the success toast.
        for (const warning of result?.warnings ?? []) notify.warning(warning)
      }
      await Promise.all([loadContacts(), loadStats()])
    } catch (err) {
      notify.error(err.message || 'Sync failed')
    } finally {
      setSyncing(false)
    }
  }

  const toggleSelected = (ids, checked) => {
    setSelected((current) => {
      const next = new Map(current)
      for (const id of ids) {
        if (!checked) { next.delete(id); continue }
        const contact = contacts.find((c) => c.id === id)
        if (contact) next.set(id, contact.email)
      }
      return next
    })
  }

  const suppress = async (targets) => {
    const emails = targets.map((t) => t.email)
    try {
      await addSuppressions(emails, 'Suppressed from the contact list')
      notify.success(
        emails.length === 1
          ? `${emails[0]} will never be mailed`
          : `${emails.length} contacts will never be mailed`,
      )
      setSelected((current) => {
        const next = new Map(current)
        for (const t of targets) next.delete(t.id)
        return next
      })
      await Promise.all([loadContacts(), loadStats()])
    } catch (err) {
      notify.error(err.message || 'Could not suppress those addresses')
    }
  }

  const submitSearch = (e) => {
    e.preventDefault()
    setPage(0)
    setAppliedSearch(search)
  }

  const changeFilter = (setter) => (e) => {
    setPage(0)
    setter(e.target.value)
  }

  // The stat tiles double as shortcuts into the list they count.
  const toggleConsent = (value) => {
    setPage(0)
    setConsent(consent === value ? '' : value)
  }

  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1)

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-5">
        <ContactStat label="Contacts" value={stats?.total ?? null} />
        <ContactStat
          label="Mailable now"
          value={stats?.mailable ?? null}
          hint={consent === MAILABLE_FILTER ? 'Showing these' : 'Default audience — click to list'}
          emphasis
          active={consent === MAILABLE_FILTER}
          onClick={() => toggleConsent(MAILABLE_FILTER)}
        />
        <ContactStat label="Opted in" value={stats?.optedIn ?? null} hint="Newsletter & Shopify" />
        <ContactStat label="App accounts" value={stats?.implied ?? null} hint="Opt-out basis" />
        <ContactStat
          label="Suppressed"
          value={stats?.suppressed ?? null}
          hint={consent === SUPPRESSED_FILTER ? 'Showing these' : 'Never mailed — click to list'}
          active={consent === SUPPRESSED_FILTER}
          onClick={() => toggleConsent(SUPPRESSED_FILTER)}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <form onSubmit={submitSearch} className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or email"
            className="pl-9"
            aria-label="Search contacts"
          />
        </form>

        <select
          value={source}
          onChange={changeFilter(setSource)}
          aria-label="Filter by source"
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">All sources</option>
          {Object.entries(SOURCE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        <select
          value={consent}
          onChange={changeFilter(setConsent)}
          aria-label="Filter by consent"
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">All consent</option>
          <option value={MAILABLE_FILTER}>Mailable now (consenting)</option>
          {Object.entries(CONSENT_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
          <option value={SUPPRESSED_FILTER}>Suppressed</option>
        </select>

        <Button variant="outline" onClick={() => setImportOpen(true)}>
          <Upload className="mr-2 h-4 w-4" />
          Import CSV
        </Button>

        <Button onClick={runSync} disabled={syncing}>
          {syncing
            ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            : <RefreshCw className="mr-2 h-4 w-4" />}
          Sync now
        </Button>
      </div>

      <ContactsSelectionBar
        count={selected.size}
        onSuppress={() => setPendingSuppress([...selected].map(([id, email]) => ({ id, email })))}
        onClear={() => setSelected(new Map())}
      />

      <ContactsTable
        contacts={contacts}
        loading={loading}
        emptyMessage={emptyMessageFor({ total, appliedSearch, source, consent })}
        selected={selected}
        onToggle={toggleSelected}
        onSuppress={(contact) => setPendingSuppress([contact])}
      />

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total.toLocaleString()}
          </span>
          <span className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </span>
        </div>
      )}

      <ImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => { loadContacts(); loadStats() }}
      />

      <ConfirmDialog
        open={!!pendingSuppress}
        onOpenChange={(open) => { if (!open) setPendingSuppress(null) }}
        title={
          pendingSuppress?.length === 1
            ? 'Stop mailing this contact?'
            : `Stop mailing ${pendingSuppress?.length ?? 0} contacts?`
        }
        description={
          pendingSuppress?.length === 1
            ? `${pendingSuppress[0].email} is excluded from every campaign from now on. You can lift an admin suppression later from the Suppressions tab.`
            : 'Every selected address is excluded from every campaign from now on. You can lift an admin suppression later from the Suppressions tab.'
        }
        confirmText="Suppress"
        onConfirm={() => { const targets = pendingSuppress; setPendingSuppress(null); suppress(targets) }}
      />
    </div>
  )
}
